import React, { useState, useEffect } from "react";
import axios from "axios";
import { 
  TrendingUp, 
  TrendingDown, 
  Plus, 
  FileText, 
  Target,
  AlertTriangle,
  CheckCircle2,
  Clock,
  BarChart3,
  Filter,
  Search,
  Edit,
  Trash2,
  Eye,
  ArrowLeft,
  Save,
  Loader2
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { confirm } from "@/lib/confirm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HistoricalBids = () => {
  const [bids, setBids] = useState([]);
  const [categories, setCategories] = useState([]);
  const [lossReasons, setLossReasons] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list"); // list, add, edit, detail
  const [selectedBid, setSelectedBid] = useState(null);
  const [filterCategory, setFilterCategory] = useState("");
  const [filterOutcome, setFilterOutcome] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    bid_name: "",
    client_name: "",
    product_category: "",
    bid_value: 0,
    bid_date: "",
    outcome: "pending",
    loss_reasons: [],
    loss_details: "",
    competitor_name: "",
    price_difference: null,
    lessons_learned: "",
    profit_margin: null
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [bidsRes, catsRes, reasonsRes] = await Promise.all([
        axios.get(`${API}/historical-bids`),
        axios.get(`${API}/product-categories`),
        axios.get(`${API}/loss-reason-categories`)
      ]);
      setBids(bidsRes.data.bids || []);
      setAnalytics(bidsRes.data.analytics || null);
      setCategories(catsRes.data || []);
      setLossReasons(reasonsRes.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (selectedBid) {
        await axios.put(`${API}/historical-bids/${selectedBid.id}`, formData);
        toast.success("Bid updated successfully");
      } else {
        await axios.post(`${API}/historical-bids`, formData);
        toast.success("Bid added successfully");
      }
      resetForm();
      setView("list");
      fetchData();
    } catch (error) {
      toast.error("Failed to save bid");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!await confirm("Delete this bid record?", { title: "Delete bid", confirmLabel: "Delete" })) return;
    try {
      await axios.delete(`${API}/historical-bids/${id}`);
      toast.success("Bid deleted");
      fetchData();
      if (view === "detail") setView("list");
    } catch (error) {
      toast.error("Failed to delete bid");
    }
  };

  const resetForm = () => {
    setFormData({
      bid_name: "",
      client_name: "",
      product_category: "",
      bid_value: 0,
      bid_date: "",
      outcome: "pending",
      loss_reasons: [],
      loss_details: "",
      competitor_name: "",
      price_difference: null,
      lessons_learned: "",
      profit_margin: null
    });
    setSelectedBid(null);
  };

  const openAddForm = () => {
    resetForm();
    setView("add");
  };

  const openEditForm = (bid) => {
    setSelectedBid(bid);
    setFormData({
      bid_name: bid.bid_name || "",
      client_name: bid.client_name || "",
      product_category: bid.product_category || "",
      bid_value: bid.bid_value || 0,
      bid_date: bid.bid_date || "",
      outcome: bid.outcome || "pending",
      loss_reasons: bid.loss_reasons || [],
      loss_details: bid.loss_details || "",
      competitor_name: bid.competitor_name || "",
      price_difference: bid.price_difference || null,
      lessons_learned: bid.lessons_learned || "",
      profit_margin: bid.profit_margin || null
    });
    setView("edit");
  };

  const openDetailView = (bid) => {
    setSelectedBid(bid);
    setView("detail");
  };

  const goBack = () => {
    setView("list");
    setSelectedBid(null);
    resetForm();
  };

  const toggleLossReason = (reason) => {
    setFormData(prev => ({
      ...prev,
      loss_reasons: prev.loss_reasons.includes(reason)
        ? prev.loss_reasons.filter(r => r !== reason)
        : [...prev.loss_reasons, reason]
    }));
  };

  const filteredBids = bids.filter(bid => {
    if (filterCategory && bid.product_category !== filterCategory) return false;
    if (filterOutcome && bid.outcome !== filterOutcome) return false;
    if (searchTerm && !bid.bid_name?.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !bid.client_name?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  const getOutcomeBadge = (outcome) => {
    switch (outcome) {
      case "won": return <Badge className="bg-emerald-100 text-emerald-700">Won</Badge>;
      case "lost": return <Badge className="bg-red-100 text-red-700">Lost</Badge>;
      default: return <Badge className="bg-amber-100 text-amber-700">Pending</Badge>;
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(value * 100000); // Convert lakhs to actual
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#007AFF]" />
      </div>
    );
  }

  // Form View (Add/Edit)
  if (view === "add" || view === "edit") {
    return (
      <div className="p-6 max-w-3xl mx-auto" data-testid="historical-bids-form">
        <Button variant="outline" onClick={goBack} className="mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to List
        </Button>

        <Card className="border-[#E2E8F0]">
          <CardHeader>
            <CardTitle>{view === "edit" ? "Edit Historical Bid" : "Add Historical Bid"}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Bid Name *</Label>
                  <Input
                    value={formData.bid_name}
                    onChange={(e) => setFormData({...formData, bid_name: e.target.value})}
                    placeholder="Project name"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Client Name *</Label>
                  <Input
                    value={formData.client_name}
                    onChange={(e) => setFormData({...formData, client_name: e.target.value})}
                    placeholder="Client/Organization"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Product Category *</Label>
                  <Select 
                    value={formData.product_category} 
                    onValueChange={(v) => setFormData({...formData, product_category: v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(cat => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Bid Value (Lakhs)</Label>
                  <Input
                    type="number"
                    value={formData.bid_value}
                    onChange={(e) => setFormData({...formData, bid_value: parseFloat(e.target.value) || 0})}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Bid Date</Label>
                  <Input
                    type="date"
                    value={formData.bid_date}
                    onChange={(e) => setFormData({...formData, bid_date: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Outcome *</Label>
                  <Select 
                    value={formData.outcome} 
                    onValueChange={(v) => setFormData({...formData, outcome: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="won">Won</SelectItem>
                      <SelectItem value="lost">Lost</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {formData.outcome === "lost" && (
                <>
                  <div className="space-y-2">
                    <Label>Loss Reasons</Label>
                    <div className="flex flex-wrap gap-2">
                      {lossReasons.map(reason => (
                        <Badge
                          key={reason}
                          variant={formData.loss_reasons.includes(reason) ? "default" : "outline"}
                          className="cursor-pointer"
                          onClick={() => toggleLossReason(reason)}
                        >
                          {reason}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Loss Details</Label>
                    <Textarea
                      value={formData.loss_details}
                      onChange={(e) => setFormData({...formData, loss_details: e.target.value})}
                      placeholder="Explain why the bid was lost..."
                      rows={3}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Competitor Name</Label>
                      <Input
                        value={formData.competitor_name}
                        onChange={(e) => setFormData({...formData, competitor_name: e.target.value})}
                        placeholder="Who won?"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Price Difference (%)</Label>
                      <Input
                        type="number"
                        value={formData.price_difference || ""}
                        onChange={(e) => setFormData({...formData, price_difference: parseFloat(e.target.value) || null})}
                        placeholder="How much lower?"
                      />
                    </div>
                  </div>
                </>
              )}

              {formData.outcome === "won" && (
                <div className="space-y-2">
                  <Label>Profit Margin (%)</Label>
                  <Input
                    type="number"
                    value={formData.profit_margin || ""}
                    onChange={(e) => setFormData({...formData, profit_margin: parseFloat(e.target.value) || null})}
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label>Lessons Learned</Label>
                <Textarea
                  value={formData.lessons_learned}
                  onChange={(e) => setFormData({...formData, lessons_learned: e.target.value})}
                  placeholder="Key takeaways from this bid..."
                  rows={3}
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={goBack}>
                  Cancel
                </Button>
                <Button type="submit" disabled={saving} className="bg-[#007AFF] hover:bg-[#0066DD]">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  {view === "edit" ? "Update" : "Create"} Bid
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Detail View
  if (view === "detail" && selectedBid) {
    const getCategoryName = (id) => categories.find(c => c.id === id)?.name || id;
    
    return (
      <div className="p-6 max-w-3xl mx-auto" data-testid="historical-bids-detail">
        <Button variant="outline" onClick={goBack} className="mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to List
        </Button>

        <Card className="border-[#E2E8F0]">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-xl">{selectedBid.bid_name}</CardTitle>
                <CardDescription>{selectedBid.client_name}</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                {getOutcomeBadge(selectedBid.outcome)}
                <Button variant="outline" size="sm" onClick={() => openEditForm(selectedBid)}>
                  <Edit className="w-4 h-4 mr-1" />
                  Edit
                </Button>
                <Button variant="outline" size="sm" className="text-red-600" onClick={() => handleDelete(selectedBid.id)}>
                  <Trash2 className="w-4 h-4 mr-1" />
                  Delete
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-[#F8FAFC] rounded-lg">
                <p className="text-sm text-[#64748B]">Product Category</p>
                <p className="font-medium">{getCategoryName(selectedBid.product_category)}</p>
              </div>
              <div className="p-4 bg-[#F8FAFC] rounded-lg">
                <p className="text-sm text-[#64748B]">Bid Value</p>
                <p className="font-medium">{formatCurrency(selectedBid.bid_value)}</p>
              </div>
              <div className="p-4 bg-[#F8FAFC] rounded-lg">
                <p className="text-sm text-[#64748B]">Bid Date</p>
                <p className="font-medium">{selectedBid.bid_date || "Not set"}</p>
              </div>
              {selectedBid.outcome === "won" && selectedBid.profit_margin && (
                <div className="p-4 bg-emerald-50 rounded-lg">
                  <p className="text-sm text-emerald-700">Profit Margin</p>
                  <p className="font-medium text-emerald-700">{selectedBid.profit_margin}%</p>
                </div>
              )}
            </div>

            {/* Loss Details */}
            {selectedBid.outcome === "lost" && (
              <>
                {selectedBid.loss_reasons?.length > 0 && (
                  <div>
                    <p className="text-sm text-[#64748B] mb-2">Loss Reasons</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedBid.loss_reasons.map(reason => (
                        <Badge key={reason} className="bg-red-100 text-red-700">{reason}</Badge>
                      ))}
                    </div>
                  </div>
                )}
                {selectedBid.loss_details && (
                  <div>
                    <p className="text-sm text-[#64748B] mb-2">Loss Details</p>
                    <p className="text-[#0F172A] bg-[#F8FAFC] p-3 rounded-lg">{selectedBid.loss_details}</p>
                  </div>
                )}
                {selectedBid.competitor_name && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-[#F8FAFC] rounded-lg">
                      <p className="text-sm text-[#64748B]">Competitor</p>
                      <p className="font-medium">{selectedBid.competitor_name}</p>
                    </div>
                    {selectedBid.price_difference && (
                      <div className="p-4 bg-[#F8FAFC] rounded-lg">
                        <p className="text-sm text-[#64748B]">Price Difference</p>
                        <p className="font-medium">{selectedBid.price_difference}% lower</p>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {/* Lessons Learned */}
            {selectedBid.lessons_learned && (
              <div>
                <p className="text-sm text-[#64748B] mb-2">Lessons Learned</p>
                <p className="text-[#0F172A] bg-blue-50 p-3 rounded-lg border border-blue-100">{selectedBid.lessons_learned}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // List View
  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="historical-bids-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-[#0F172A] font-['Manrope'] flex items-center gap-3">
            <BarChart3 className="w-7 h-7 text-[#007AFF]" />
            Historical Bids
          </h1>
          <p className="text-[#64748B] mt-1">
            Track past bids to improve future predictions
          </p>
        </div>
        <Button onClick={openAddForm} className="bg-[#007AFF] hover:bg-[#0066DD]">
          <Plus className="w-4 h-4 mr-2" />
          Add Historical Bid
        </Button>
      </div>

      {/* Analytics */}
      {analytics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="border-[#E2E8F0]">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-[#007AFF]/10 flex items-center justify-center">
                <FileText className="w-6 h-6 text-[#007AFF]" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#0F172A]">{analytics.total_bids}</p>
                <p className="text-sm text-[#64748B]">Total Bids</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-[#E2E8F0]">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-emerald-600">{analytics.win_rate?.toFixed(1)}%</p>
                <p className="text-sm text-[#64748B]">Win Rate</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-[#E2E8F0]">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-emerald-600">{analytics.won_bids}</p>
                <p className="text-sm text-[#64748B]">Won</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-[#E2E8F0]">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <TrendingDown className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-red-600">{analytics.lost_bids}</p>
                <p className="text-sm text-[#64748B]">Lost</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="border-[#E2E8F0] mb-6">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                <Input
                  placeholder="Search bids..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={filterCategory || "all"} onValueChange={(v) => setFilterCategory(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterOutcome || "all"} onValueChange={(v) => setFilterOutcome(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="All Outcomes" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Outcomes</SelectItem>
                <SelectItem value="won">Won</SelectItem>
                <SelectItem value="lost">Lost</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Bids List */}
      <Card className="border-[#E2E8F0]">
        <CardHeader>
          <CardTitle>Bid Records</CardTitle>
          <CardDescription>{filteredBids.length} bids found</CardDescription>
        </CardHeader>
        <CardContent>
          {filteredBids.length === 0 ? (
            <div className="text-center py-12 text-[#64748B]">
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium">No bids found</p>
              <p className="text-sm">Add historical bids to track your performance</p>
              <Button onClick={openAddForm} className="mt-4">
                <Plus className="w-4 h-4 mr-2" />
                Add First Bid
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredBids.map(bid => (
                <div 
                  key={bid.id}
                  className="p-4 border border-[#E2E8F0] rounded-lg hover:border-[#007AFF]/50 hover:shadow-sm transition-all"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="font-bold text-[#0F172A]">{bid.bid_name}</h3>
                        {getOutcomeBadge(bid.outcome)}
                      </div>
                      <p className="text-sm text-[#64748B]">{bid.client_name}</p>
                      <div className="flex items-center gap-4 mt-2 text-sm">
                        <span className="text-[#64748B]">
                          {categories.find(c => c.id === bid.product_category)?.name || bid.product_category}
                        </span>
                        <span className="text-[#64748B]">•</span>
                        <span className="font-medium">{formatCurrency(bid.bid_value)}</span>
                        {bid.bid_date && (
                          <>
                            <span className="text-[#64748B]">•</span>
                            <span className="text-[#64748B]">{bid.bid_date}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm" onClick={() => openDetailView(bid)}>
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => openEditForm(bid)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDelete(bid.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default HistoricalBids;
